import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';
import { Search, Calendar, FileText, AlertCircle, RefreshCw } from 'lucide-react';
import '../styles/useraccounts.css';

const EmployeePayrollStatement = ({ employeeInfo, userInfo }) => {
  const [statements, setStatements] = useState([]);
  const [placement, setPlacement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchStatements();
  }, [employeeInfo]);

  const fetchStatements = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const empId = employeeInfo?.EmployeeId || employeeInfo?.employeeId || userInfo?.EmployeeId || userInfo?.employeeId;

      const response = await axios.get(`${BASE_URL}/api/accounts/my-statements`, {
        params: {
          employeeId: empId,
          companyId: employeeInfo?.companyId
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data && response.data.success) {
        setStatements(response.data.data || []);
        setPlacement(response.data.placement || null);
      } else {
        setStatements([]);
        setPlacement(null);
      }
    } catch (err) {
      console.error('Error fetching employee statements:', err);
      setError('Unable to load payroll statements. Please try again later.');
      setStatements([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredStatements = statements.filter((statement) => {
    const periodStr = statement.PeriodStartDate && statement.PeriodEndDate
      ? `${new Date(statement.PeriodStartDate).toLocaleDateString()} - ${new Date(statement.PeriodEndDate).toLocaleDateString()}`
      : statement.Period || statement.Reference || 'N/A';

    const matchesSearch =
      (statement.Description?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      periodStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (statement.Reference?.toLowerCase() || '').includes(searchTerm.toLowerCase());

    let matchesDate = true;
    if (startDate) {
      matchesDate = matchesDate && new Date(statement.CheckDate || statement.PeriodStartDate) >= new Date(startDate);
    }
    if (endDate) {
      const endD = new Date(endDate);
      endD.setHours(23, 59, 59, 999);
      matchesDate = matchesDate && new Date(statement.CheckDate || statement.PeriodEndDate) <= endD;
    }
    return matchesSearch && matchesDate;
  });

  return (
    <div className="accounts-tab-content" style={{ marginTop: '1rem', background: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>
            Payroll Statement - {employeeInfo?.Name || employeeInfo?.name || userInfo?.name || 'Employee'}
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.9rem' }}>
            View your verified client hours, pay rate, and earnings statements
          </p>
        </div>
        <button
          onClick={fetchStatements}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            background: '#fff',
            color: '#475569',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: 500
          }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Filter Bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div style={{ position: 'relative', flex: '1 1 280px' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Search by description, reference, or period..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 36px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.9rem',
              outline: 'none'
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Start Date:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>End Date:</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem' }}
            />
          </div>
        </div>
      </div>

      {/* Placement Details Banner (Vendor, Bill Rate, Candidate Rate, Job Start Date) */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        padding: '12px 16px',
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        marginBottom: '1rem',
        fontSize: '0.875rem'
      }}>
        <div>
          <span style={{ fontWeight: 600, color: '#475569', marginRight: '6px' }}>Vendor:</span>
          <span style={{ color: '#1e293b', fontWeight: 600 }}>{placement?.Vendor || placement?.vendor || 'Alku'}</span>
        </div>
        <div>
          <span style={{ fontWeight: 600, color: '#475569', marginRight: '6px' }}>Bill Rate:</span>
          <span style={{ color: '#1e293b', fontWeight: 600 }}>${parseFloat(placement?.BillRate || placement?.billRate || 57).toFixed(2)}/hr</span>
        </div>
        <div>
          <span style={{ fontWeight: 600, color: '#475569', marginRight: '6px' }}>Candidate Rate:</span>
          <span style={{ color: '#059669', fontWeight: 600 }}>${parseFloat(placement?.CandidateRate || placement?.candidateRate || 45.6).toFixed(2)}/hr</span>
        </div>
        <div>
          <span style={{ fontWeight: 600, color: '#475569', marginRight: '6px' }}>Job Start:</span>
          <span style={{ color: '#1e293b' }}>{placement?.JobStartDate ? new Date(placement.JobStartDate).toLocaleDateString() : '07/20/2026'}</span>
        </div>
        <div>
          <span style={{ fontWeight: 600, color: '#475569', marginRight: '6px' }}>Pay Frequency:</span>
          <span style={{ color: '#1e293b' }}>Monthly</span>
        </div>
        <div>
          <span style={{ fontWeight: 600, color: '#475569', marginRight: '6px' }}>Employee:</span>
          <span style={{ color: '#1e293b', fontWeight: 600 }}>{employeeInfo?.Name || employeeInfo?.name || userInfo?.name || 'Consultant'}</span>
        </div>
      </div>

      {/* Meta Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: '#f8fafc', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem', color: '#475569' }}>
        <span>Showing <strong>{filteredStatements.length}</strong> of <strong>{statements.length}</strong> statements</span>
      </div>

      {/* Table Area */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
          <p>Loading payroll statements...</p>
        </div>
      ) : error ? (
        <div style={{ padding: '30px', textAlign: 'center', color: '#ef4444' }}>
          <AlertCircle size={32} style={{ margin: '0 auto 8px auto' }} />
          <p>{error}</p>
        </div>
      ) : filteredStatements.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table className="statements-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#3b82f6', color: '#fff' }}>
                <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600 }}>Date</th>
                <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600 }}>Ref</th>
                <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600 }}>Description</th>
                <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '0.85rem', fontWeight: 600 }}>Hours</th>
                <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '0.85rem', fontWeight: 600 }}>Pay Rate</th>
                <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '0.85rem', fontWeight: 600 }}>Credit</th>
                <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '0.85rem', fontWeight: 600 }}>Debit</th>
                <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '0.85rem', fontWeight: 600 }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {filteredStatements.map((stmt, idx) => {
                const dateVal = stmt.CheckDate || stmt.PeriodEndDate || stmt.PeriodStartDate;
                const dateStr = dateVal ? new Date(dateVal).toLocaleDateString() : '-';
                const refStr = stmt.Reference || stmt.Period || '-';
                const descStr = stmt.Description || 'Payroll Statement';
                const hoursVal = stmt.Hours !== undefined && stmt.Hours !== null ? parseFloat(stmt.Hours).toFixed(2) : '-';
                const rateVal = stmt.PayRate ? `$${parseFloat(stmt.PayRate).toFixed(2)}` : '-';
                const creditVal = stmt.Credit !== undefined && stmt.Credit !== null ? `$${parseFloat(stmt.Credit).toFixed(2)}` : '$0.00';
                const debitVal = stmt.Debit !== undefined && stmt.Debit !== null ? `$${parseFloat(stmt.Debit).toFixed(2)}` : '$0.00';
                const balanceVal = stmt.Balance !== undefined && stmt.Balance !== null ? `$${parseFloat(stmt.Balance).toFixed(2)}` : creditVal;

                return (
                  <tr key={stmt.Id || stmt.StatementId || idx} style={{ borderBottom: '1px solid #e2e8f0', background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td style={{ padding: '12px 14px', fontSize: '0.85rem', color: '#1e293b' }}>{dateStr}</td>
                    <td style={{ padding: '12px 14px', fontSize: '0.85rem', color: '#64748b' }}>{refStr}</td>
                    <td style={{ padding: '12px 14px', fontSize: '0.85rem', color: '#1e293b', fontWeight: 500 }}>{descStr}</td>
                    <td style={{ padding: '12px 14px', fontSize: '0.85rem', color: '#1e293b', textAlign: 'right' }}>{hoursVal}</td>
                    <td style={{ padding: '12px 14px', fontSize: '0.85rem', color: '#1e293b', textAlign: 'right' }}>{rateVal}</td>
                    <td style={{ padding: '12px 14px', fontSize: '0.85rem', color: '#10b981', fontWeight: 600, textAlign: 'right' }}>{creditVal}</td>
                    <td style={{ padding: '12px 14px', fontSize: '0.85rem', color: '#ef4444', textAlign: 'right' }}>{debitVal}</td>
                    <td style={{ padding: '12px 14px', fontSize: '0.85rem', color: '#1e293b', fontWeight: 700, textAlign: 'right' }}>{balanceVal}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ padding: '50px 20px', textAlign: 'center', color: '#94a3b8' }}>
          <FileText size={48} style={{ margin: '0 auto 12px auto', strokeWidth: 1.5, color: '#cbd5e1' }} />
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#64748b', margin: '0 0 6px 0' }}>No Statements Found</h3>
          <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: 0 }}>
            Once your external timesheet is processed and approved, your statements will appear here.
          </p>
        </div>
      )}
    </div>
  );
};

export default EmployeePayrollStatement;
